import type { ReactNode } from "react"
import type { AudienceFilter, AudienceSegment } from "@/entities/audience"
import type { BroadcastContext, BroadcastDraftContent } from "@/entities/broadcast"
import { Field } from "@/shared/ui/form-field"
import { DateTimeField } from "@/shared/ui/date-time-field"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { defaultSendAt, TIME_ZONE } from "../lib/schedule"
import { AudienceSection } from "./audience-section"
import { MessageSection } from "./message-section"
import { Section } from "./section"
import { LoadSwap } from "@/shared/ui/load-swap"

export { defaultSendAt, TIME_ZONE } from "../lib/schedule"

const SEND_MODES = [
	{ value: "now", label: "Сейчас" },
	{ value: "scheduled", label: "По расписанию" },
] as const

interface BroadcastFormProps {
	botId: string
	content: BroadcastDraftContent
	filter: AudienceFilter
	context: BroadcastContext
	recipients: number | undefined
	counting?: boolean
	onContent: (content: BroadcastDraftContent) => void
	onFilter: (filter: AudienceFilter) => void
	/** Когда отправить: пусто: сразу, иначе значение datetime-local во времени устройства. */
	sendAt: string
	onSendAt: (value: string) => void
	/** «Кому уйдёт рассылка» одной фразой: считает страница, у неё есть описание фильтра по переменной. */
	filterDescription: string
	renderSegmentFilters: (
		value: AudienceSegment,
		onChange: (next: AudienceSegment) => void,
		hasContext: boolean,
	) => ReactNode
	/** Сохранённые выборки над своими условиями. */
	savedFilters?: ReactNode
}

/** Черновик рассылки: сообщение с файлом и кнопками, кому и когда отправить. Название правится в шапке. */
export function BroadcastForm({
	botId,
	content,
	filter,
	context,
	recipients,
	counting,
	onContent,
	onFilter,
	sendAt,
	onSendAt,
	filterDescription,
	renderSegmentFilters,
	savedFilters,
}: BroadcastFormProps) {
	// AudienceVariable не хранит id: переменная сценария и так уникальна по имени.
	const variables = context.variables.map((variable) => ({
		id: variable.name,
		name: variable.name,
	}))

	return (
		<div className="space-y-4">
			<MessageSection
				botId={botId}
				content={content}
				onContent={onContent}
				variables={variables}
				customFlows={context.flows}
			/>

			<AudienceSection
				botId={botId}
				filter={filter}
				onFilter={onFilter}
				recipients={recipients}
				counting={counting}
				filterDescription={filterDescription}
				tags={context.tags}
				renderSegmentFilters={renderSegmentFilters}
				savedFilters={savedFilters}
			/>

			<Section title="Когда" hint={`Время по часовому поясу ${TIME_ZONE}`}>
				<SegmentedControl
					aria-label="Когда отправить"
					options={SEND_MODES}
					value={sendAt ? "scheduled" : "now"}
					onChange={(value) => onSendAt(value === "scheduled" ? sendAt || defaultSendAt() : "")}
				/>
				<LoadSwap loading={false} id={sendAt ? "scheduled" : "now"}>
					{sendAt ? (
						<Field label="Дата и время" htmlFor="broadcast-send-at">
							<DateTimeField
								id="broadcast-send-at"
								value={sendAt}
								min={new Date()}
								onChange={onSendAt}
							/>
						</Field>
					) : (
						<p className="text-sm text-muted-foreground">
							Сообщение уйдёт сразу, как только вы подтвердите отправку.
						</p>
					)}
				</LoadSwap>
			</Section>
		</div>
	)
}
